import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useCaseStreaming,
  useCareCaseService,
  useRegistrationService,
  useRegistrationStreaming,
  type CareCaseListView,
  type RegistrationListView,
} from '@actschurch/ccms-sdk';
import type { BreakdownDatum } from '../Components/BreakdownBars';
import { TREND_MONTHS, type OverviewDomain } from '../constants';

/**
 * useOverviewCharts — every breakdown/trend series on the Overview page, in one place.
 *
 * The aggregates these charts want are specced in `_deploy/_newEndPoints/` but do not
 * exist in the SDK yet (`@actschurch/ccms-sdk` is pinned at ^0.0.37). This hook is the
 * single seam that hides that:
 *
 *  - Where a client-side fallback is possible (cases by need/zone, registrations by
 *    course, the activity mix) we group the streamed lists that the admin list pages
 *    already load. One stream per module serves several charts.
 *  - Where it is not (both location breakdowns, both monthly trends) the chart reports
 *    `unavailable` and renders an explanatory empty state rather than an empty axis.
 *
 * Detection is by capability, not version — `typeof service.statsByNeed === 'function'` —
 * so bumping the SDK lights the server-side path up with no further change here.
 */

/** A breakdown series plus why it is empty, if it is. */
export interface ChartSeries {
  data: BreakdownDatum[];
  /** Set when there is no data source at all; the chart shows this instead of an axis. */
  unavailable?: string;
}

export interface TrendPoint {
  monthDisplay: string;
  opened: number;
  closed: number;
}

export interface ActivitySegment {
  status: string;
  color: string;
  count: number;
}

const AWAITING_AGGREGATE =
  'This breakdown needs a server-side aggregate that is not in the SDK yet — see _deploy/_newEndPoints/.';

/** Groups rows by a lookup id/name/colour triple into sorted breakdown data. */
function groupBy<T>(
  rows: T[],
  getId: (row: T) => number | null | undefined,
  getName: (row: T) => string | null | undefined,
  getColor: (row: T) => string | null | undefined,
): BreakdownDatum[] {
  const buckets = new Map<number, BreakdownDatum>();

  rows.forEach((row) => {
    const id = getId(row) ?? 0;
    const existing = buckets.get(id);
    if (existing) {
      existing.value += 1;
      existing.total = (existing.total ?? 0) + 1;
      return;
    }
    buckets.set(id, {
      id,
      name: getName(row) ?? 'Unassigned',
      color: getColor(row) ?? null,
      value: 1,
      total: 1,
    });
  });

  return [...buckets.values()].sort((a, b) => b.value - a.value);
}

export interface UseOverviewChartsResult {
  casesByNeed: ChartSeries;
  casesByZone: ChartSeries;
  casesByLocation: ChartSeries;
  registrationsByCourse: ChartSeries;
  registrationsByLocation: ChartSeries;
  caseTrend: { data: TrendPoint[]; unavailable?: string };
  activityMix: ActivitySegment[];
  loading: boolean;
  refresh: () => void;
}

export function useOverviewCharts(domain: OverviewDomain): UseOverviewChartsResult {
  const careCaseService = useCareCaseService();
  const registrationService = useRegistrationService();

  const wantsCare = domain === 'care';
  const wantsCourse = domain === 'course';

  // Server-side aggregates, when the SDK exposes them.
  const [serverSeries, setServerSeries] = useState<Record<string, BreakdownDatum[]>>({});
  const [serverTrend, setServerTrend] = useState<TrendPoint[] | null>(null);

  // Fallback: the same streams the admin list pages already use. One per module,
  // shared across every chart derived from it.
  const {
    items: cases,
    isLoading: casesLoading,
    load: loadCases,
  } = useCaseStreaming(undefined, 50);

  const {
    items: registrations,
    isLoading: registrationsLoading,
    load: loadRegistrations,
  } = useRegistrationStreaming(undefined, 50);

  const fetchServerSeries = useCallback(async () => {
    const svc = careCaseService as any;
    const reg = registrationService as any;
    const next: Record<string, BreakdownDatum[]> = {};

    const calls: Array<Promise<void>> = [];

    const collect = (
      key: string,
      fn: (() => Promise<any[]>) | undefined,
      map: (row: any) => BreakdownDatum,
    ) => {
      if (typeof fn !== 'function') return;
      calls.push(
        fn()
          .then((rows) => { next[key] = (rows ?? []).map(map); })
          .catch((e) => console.error(`Error fetching ${key}:`, e)),
      );
    };

    const careMap = (row: any): BreakdownDatum => ({
      id: row.id, name: row.name, color: row.color, value: row.openCount, total: row.totalCount,
    });
    const courseMap = (row: any): BreakdownDatum => ({
      id: row.id, name: row.name, color: row.color, value: row.activeCount, total: row.totalCount,
    });

    if (wantsCare) {
      collect('casesByNeed', svc.statsByNeed?.bind(svc), careMap);
      collect('casesByZone', svc.statsByZone?.bind(svc), careMap);
      collect('casesByLocation', svc.statsByLocation?.bind(svc), careMap);
    }

    if (wantsCourse) {
      collect('registrationsByCourse', reg.statsByCourse?.bind(reg), courseMap);
      collect('registrationsByLocation', reg.statsByLocation?.bind(reg), courseMap);
    }

    if (wantsCare && typeof svc.statsMonthly === 'function') {
      calls.push(
        svc.statsMonthly(TREND_MONTHS)
          .then((rows: any[]) => {
            setServerTrend((rows ?? []).map((r) => ({
              monthDisplay: r.monthDisplay ?? String(r.yearMonth),
              opened: r.openedCount,
              closed: r.closedCount,
            })));
          })
          .catch((e: unknown) => console.error('Error fetching case trend:', e)),
      );
    }

    if (calls.length === 0) return;
    await Promise.all(calls);
    setServerSeries(next);
  }, [careCaseService, registrationService, wantsCare, wantsCourse]);

  // Only the stream this page actually charts is loaded. The other hook is still called, as the
  // rules of hooks require, but its `load()` is never invoked so it makes no requests.
  const refresh = useCallback(() => {
    fetchServerSeries();
    if (wantsCare) loadCases();
    if (wantsCourse) loadRegistrations();
  }, [fetchServerSeries, loadCases, loadRegistrations, wantsCare, wantsCourse]);

  useEffect(() => { fetchServerSeries(); }, [fetchServerSeries]);
  useEffect(() => { if (wantsCare) loadCases(); }, [wantsCare, loadCases]);
  useEffect(() => { if (wantsCourse) loadRegistrations(); }, [wantsCourse, loadRegistrations]);

  // ── Derived series ────────────────────────────────────────────────────────
  // Server aggregate wins; otherwise group the stream; otherwise report unavailable.

  const casesByNeed = useMemo<ChartSeries>(() => ({
    data: serverSeries.casesByNeed
      ?? groupBy(cases as CareCaseListView[], (c) => c.needID, (c) => c.needName, (c) => c.needColor),
  }), [serverSeries.casesByNeed, cases]);

  const casesByZone = useMemo<ChartSeries>(() => ({
    data: serverSeries.casesByZone
      ?? groupBy(cases as CareCaseListView[], (c) => c.zoneID, (c) => c.zoneName, () => null),
  }), [serverSeries.casesByZone, cases]);

  // `CareCaseListView` carries no location field, so there is nothing to group.
  const casesByLocation = useMemo<ChartSeries>(() => (
    serverSeries.casesByLocation
      ? { data: serverSeries.casesByLocation }
      : { data: [], unavailable: AWAITING_AGGREGATE }
  ), [serverSeries.casesByLocation]);

  const registrationsByCourse = useMemo<ChartSeries>(() => ({
    data: serverSeries.registrationsByCourse
      ?? groupBy(
        registrations as RegistrationListView[],
        (r) => r.courseID, (r) => r.courseName, (r) => r.courseColor,
      ),
  }), [serverSeries.registrationsByCourse, registrations]);

  // `RegistrationListView` carries only `dateID`; location lives on `CourseDate`.
  const registrationsByLocation = useMemo<ChartSeries>(() => (
    serverSeries.registrationsByLocation
      ? { data: serverSeries.registrationsByLocation }
      : { data: [], unavailable: AWAITING_AGGREGATE }
  ), [serverSeries.registrationsByLocation]);

  const caseTrend = useMemo(() => (
    serverTrend
      ? { data: serverTrend }
      : {
          data: [] as TrendPoint[],
          unavailable:
            'The opened-vs-closed trend needs a server-side monthly aggregate — see _deploy/_newEndPoints/.',
        }
  ), [serverTrend]);

  /**
   * Activity mix — `activityStatus` / `activityStatusColor` are computed on
   * `CareCaseListView`, so this one works entirely from the stream today.
   */
  const activityMix = useMemo<ActivitySegment[]>(() => {
    const buckets = new Map<string, ActivitySegment>();
    (cases as CareCaseListView[]).forEach((c) => {
      const status = c.activityStatus ?? 'Unknown';
      const existing = buckets.get(status);
      if (existing) { existing.count += 1; return; }
      buckets.set(status, {
        status,
        color: c.activityStatusColor ?? 'var(--mantine-color-dimmed)',
        count: 1,
      });
    });
    return [...buckets.values()].sort((a, b) => b.count - a.count);
  }, [cases]);

  return {
    casesByNeed,
    casesByZone,
    casesByLocation,
    registrationsByCourse,
    registrationsByLocation,
    caseTrend,
    activityMix,
    loading: wantsCare ? casesLoading : registrationsLoading,
    refresh,
  };
}

export default useOverviewCharts;
